import { graphQlClient } from '@config/graphqlClient';
import ActivitiesGraphQL from '@graphql/activity.queries';
import { Activity } from '@models/activity.model';

export type PagedActivities = {
  total: number;
  items: Activity[];
  // Keyset (cursor) pagination — present once the backend keyset path is used.
  nextCursor?: string | null;
  hasMore?: boolean;
};

export const activityService = {
  async getActivitiesByProjects(
    projectsId: string[],
    page?: number,
    limit?: number,
    types?: string[],
    after?: string,
    resolutionStatus?: string,
  ): Promise<PagedActivities> {
    if (!projectsId || projectsId.length === 0)
      throw new Error('projectsId is required');

    const data: any = await graphQlClient.request(
      ActivitiesGraphQL.queries.getActivitiesByProjects,
      {
        projectsId,
        page,
        limit,
        types: types && types.length > 0 ? types : undefined,
        after,
        resolutionStatus,
      },
    );

    return (
      data?.getActivitiesByProjects ?? {
        total: 0,
        items: [],
      }
    );
  },

  async getPendingInvoicesCountByProjects(projectsId: string[]): Promise<number> {
    if (!projectsId || projectsId.length === 0) return 0;

    const data: any = await graphQlClient.request(
      ActivitiesGraphQL.queries.getPendingInvoicesCountByProjects,
      { projectsId },
    );

    return data?.getPendingInvoicesCountByProjects ?? 0;
  },

  async getActivitiesByOrganizationId(
    organizationId: string,
    page?: number,
    limit?: number,
    after?: string,
  ): Promise<PagedActivities> {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    const data: any = await graphQlClient.request(
      ActivitiesGraphQL.queries.getActivitiesByOrganizationId,
      {
        organizationId,
        page,
        limit,
        after,
      },
    );

    return (
      data?.getActivitiesByOrganizationId ?? {
        total: 0,
        items: [],
      }
    );
  },

  async getActivitiesByOrganizationIdAndProjects(
    organizationId: string,
    projectIds: string[],
    page?: number,
    limit?: number,
    after?: string,
  ): Promise<PagedActivities> {
    if (!organizationId || !projectIds) {
      throw new Error('Organization ID and Project ID are required');
    }

    const data: any = await graphQlClient.request(
      ActivitiesGraphQL.queries.getActivitiesByOrganizationIdAndProjects,
      {
        organizationId,
        projectIds,
        page,
        limit,
        after,
      },
    );

    return (
      data?.getActivitiesByOrganizationIdAndProjects ?? {
        total: 0,
        items: [],
      }
    );
  },
};

export default activityService;
